import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import businessFlowsTheme, { brand } from "../../theme/businessFlowsTheme";
import { canOpenConsole, canPlatform, editableDatabases, platformRoleLabel, staffRoleOf, viewDatabases } from "../../utils/permissions";
import useBranding from "../../utils/branding";
import AdminOverview from "./AdminOverview";
import WorkspacesPage from "./workspaces/WorkspacesPage";
import AllUsersPage from "./workspaces/AllUsersPage";
import DatabasesPage from "./databases/DatabasesPage";
import ContentManager from "./content/ContentManager";
// The Properties panel screen is not a console section any more: Display
// renders it inside its Fields tab (admin/display/FieldsTab.jsx).
import DisplayPage from "./display/DisplayPage";
import ReleaseNotesUpdate from "../login/admin/ReleaseNotesUpdate";
import PlansEntitlements from "./PlansEntitlements";
import FeatureSwitches from "./FeatureSwitches";
import ActivityPage from "./activity/ActivityPage";
import PlatformSettings from "./PlatformSettings";
import DevelopersPage from "../developers/DevelopersPage";
import { SHOW_DEVELOPER_API } from "../../features";
import "../login/admin/admin.css";
import "../auth/auth.css";

// route: the path segment after /admin. permission: what a staff member needs
// to see it. forEditors: also shown to customers who edit a database;
// forWorkspaceAdmins: also shown to Workspace admins of a database.
// aliases: earlier addresses of the screen.
const SECTIONS = [
  {
    heading: null,
    items: [{ key: "overview", route: "", label: "Overview", icon: <DashboardRoundedIcon />, permission: "platform.view" }],
  },
  {
    heading: "People",
    items: [
      {
        key: "workspaces",
        route: "workspaces",
        label: "Workspaces",
        icon: <WorkspacesRoundedIcon />,
        permission: "tenants.view",
        aliases: ["organisations"],
      },
      { key: "users", route: "users", label: "All users", icon: <GroupRoundedIcon />, permission: "users.view", aliases: ["staff"] },
    ],
  },
  {
    heading: "Data",
    items: [{ key: "databases", route: "databases", label: "Databases", icon: <StorageOutlinedIcon />, permission: "tenants.view" }],
  },
  {
    heading: "Content",
    items: [
      { key: "upload", route: "upload-bpmn", label: "Objects and diagrams", icon: <UploadFileRoundedIcon />, permission: "content.manage", forEditors: true },
      // How one database looks to everyone who reads it: names, colours,
      // fields, diagram and map settings. Not the workspace's Views, which is
      // a whitelist per group and lives beside that workspace's groups. The
      // screen was called Views until 24 Sep 2026 and kept its old addresses.
      {
        key: "display",
        route: "display",
        label: "Display",
        icon: <PaletteOutlinedIcon />,
        permission: "platform.view",
        forWorkspaceAdmins: true,
        aliases: ["views", "attribute-mapping", "advance-search"],
      },
      { key: "release", route: "update-releasenotes", label: "Release notes", icon: <CampaignRoundedIcon />, permission: "releaseNotes.manage" },
    ],
  },
  {
    heading: "Platform",
    items: [
      // Which of the product's features this instance offers at all. Support
      // looks (platform.view, as on Plans and entitlements and Activity); only
      // settings.manage changes one, which is what POST /api/admin/features
      // asks for - the screen reads to everyone who may open the console's
      // Platform section and fixes its switches for the rest.
      { key: "features", route: "features", label: "Features", icon: <TuneRoundedIcon />, permission: "platform.view" },
      { key: "plans", route: "plans", label: "Plans and entitlements", icon: <WorkspacePremiumRoundedIcon />, permission: "platform.view" },
      { key: "activity", route: "activity", label: "Activity", icon: <HistoryRoundedIcon />, permission: "platform.view" },
      // Lean UI for the trade fair: SHOW_DEVELOPER_API=false hides the
      // Developers section. Flip it in ../../features to bring it back.
      ...(SHOW_DEVELOPER_API
        ? [{ key: "developers", route: "developers", label: "Developers", icon: <CodeRoundedIcon />, permission: "platform.view" }]
        : []),
      { key: "settings", route: "settings", label: "Platform settings", icon: <SettingsOutlinedIcon />, permission: "settings.manage" },
    ],
  },
];

const allItems = SECTIONS.flatMap((section) => section.items);

const mayOpen = (user, item) =>
  canPlatform(user, item.permission) ||
  Boolean(item.forEditors && editableDatabases(user).length) ||
  Boolean(item.forWorkspaceAdmins && viewDatabases(user).length);

function AccessDenied({ onLeave, dedicated }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: "100%", p: 4, textAlign: "center" }}>
      <Box
        aria-hidden
        sx={{ width: 52, height: 52, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: brand.shell, border: `1px solid ${brand.hairline}`, color: brand.muted }}
      >
        <LockRoundedIcon />
      </Box>
      <Typography variant="h6" component="h1" sx={{ fontWeight: 700 }}>
        {dedicated ? "This area is for instance admins" : "This area is for bpExperts staff"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
        Your account can't change anything here. Workspace admins manage their people from Manage workspace in the account menu.
      </Typography>
      <Button variant="contained" onClick={onLeave}>
        Back to Business Flows
      </Button>
    </Stack>
  );
}

export default function AdminConsole({ user = {} }) {
  const { instanceMode } = useBranding();
  const history = useHistory();
  const location = useLocation();
  const wide = useMediaQuery(businessFlowsTheme.breakpoints.up("md"));
  const [navOpen, setNavOpen] = useState(false);

  const route = location.pathname.split("/")[2] || "";
  const visibleSections = useMemo(
    () =>
      SECTIONS.map((section) => ({ ...section, items: section.items.filter((item) => mayOpen(user, item)) })).filter(
        (section) => section.items.length
      ),
    [user]
  );
  const firstVisible = visibleSections[0]?.items[0] || allItems[0];
  const matches = (item) => item.route === route || Boolean(route && item.aliases?.includes(route));
  // A screen the person may not open shows Access denied; /admin alone opens their first screen.
  const current = allItems.find((item) => matches(item) && mayOpen(user, item)) || (route ? allItems.find(matches) : null) || firstVisible;
  const allowed = mayOpen(user, current);
  // Staff see their bpExperts role; customers who edit a database see "Editor".
  const roleLabel = staffRoleOf(user.platformRole) ? platformRoleLabel(user.platformRole, instanceMode) : "Editor";

  const go = (item) => {
    setNavOpen(false);
    history.push(`/admin${item.route ? `/${item.route}` : ""}`);
  };

  const nav = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 2, py: 2 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: brand.ink, color: brand.signal }}>
          <AdminPanelSettingsRoundedIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>Admin console</Typography>
          <Typography variant="caption" color="text.secondary">
            {roleLabel}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1, pb: 1 }}>
        {visibleSections.map((section) => (
          <Box key={section.heading || "main"} sx={{ mb: 1 }}>
            {section.heading && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", px: 1.5, pt: 1.75, pb: 0.5, fontWeight: 650 }}
              >
                {section.heading}
              </Typography>
            )}
            <List disablePadding>
              {section.items.map((item) => (
                <ListItemButton
                  key={item.key}
                  selected={item.key === current.key}
                  onClick={() => go(item)}
                  sx={{
                    borderRadius: 2.5,
                    mb: 0.25,
                    "&.Mui-selected": { bgcolor: brand.ink, color: "#fff", "&:hover": { bgcolor: brand.inkSoft } },
                    "&.Mui-selected .MuiListItemIcon-root": { color: brand.signal },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>{item.icon}</ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: item.key === current.key ? 700 : 500 }} primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 1.5, borderTop: `1px solid ${brand.hairline}` }}>
        <Button fullWidth startIcon={<ArrowBackRoundedIcon />} onClick={() => history.push("/home")} sx={{ justifyContent: "flex-start" }}>
          Back to Business Flows
        </Button>
      </Box>
    </Box>
  );

  const panel = () => {
    if (!allowed) return <AccessDenied onLeave={() => history.push("/home")} dedicated={instanceMode === "dedicated"} />;
    switch (current.key) {
      case "overview":
        return <AdminOverview user={user} onOpen={(key) => go(allItems.find((i) => i.key === key) || allItems[0])} />;
      case "workspaces":
        return <WorkspacesPage user={user} />;
      case "users":
        return <AllUsersPage user={user} />;
      case "databases":
        return <DatabasesPage user={user} />;
      case "upload":
        return <ContentManager user={user} />;
      case "display":
        return <DisplayPage user={user} />;
      case "release":
        return <ReleaseNotesUpdate />;
      case "features":
        return <FeatureSwitches user={user} />;
      case "plans":
        return <PlansEntitlements user={user} />;
      case "activity":
        return <ActivityPage />;
      case "developers":
        return <DevelopersPage user={user} />;
      case "settings":
        return <PlatformSettings />;
      default:
        return null;
    }
  };

  if (!canOpenConsole(user)) {
    return (
      <ThemeProvider theme={businessFlowsTheme}>
        <Box className="bfx" sx={{ flex: 1, minHeight: 0, height: "100%", bgcolor: "background.default" }}>
          <AccessDenied onLeave={() => history.push("/home")} dedicated={instanceMode === "dedicated"} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={businessFlowsTheme}>
      <Box
        className="bfx"
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          boxSizing: "border-box",
          bgcolor: "background.default",
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "264px minmax(0, 1fr)" },
          gap: { xs: 0, md: 2 },
          p: { xs: 0, md: 2 },
        }}
      >
        {wide ? (
          <Paper elevation={0} sx={{ borderRadius: 4, border: `1px solid ${brand.line}`, bgcolor: brand.shell, overflow: "hidden", minHeight: 0 }}>
            {nav}
          </Paper>
        ) : (
          <Paper
            elevation={0}
            square
            sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, bgcolor: brand.shell, borderBottom: `1px solid ${brand.line}` }}
          >
            <IconButton onClick={() => setNavOpen(true)} aria-label="Admin sections">
              <MenuRoundedIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 700 }}>{current.label}</Typography>
            <Chip
              size="small"
              label={roleLabel}
              // A tint of the brand yellow: it says who you are, it is not the
              // loudest thing on the screen.
              sx={{ ml: "auto", bgcolor: "rgba(240, 230, 49, 0.35)", border: `1px solid ${brand.signalDeep}`, color: brand.ink, fontWeight: 600 }}
            />
          </Paper>
        )}

        <Box
          sx={{
            minHeight: 0,
            overflowY: "auto",
            borderRadius: { md: 4 },
            border: { md: `1px solid ${brand.line}` },
            bgcolor: "#fff",
          }}
        >
          <Box className="adm adm--console">{panel()}</Box>
        </Box>
      </Box>

      {!wide && (
        <Drawer anchor="left" open={navOpen} onClose={() => setNavOpen(false)} PaperProps={{ className: "bfx", sx: { width: 272 } }}>
          {nav}
        </Drawer>
      )}
    </ThemeProvider>
  );
}
